import '@/api/workspace/endpoints/arkConfig.ts';
import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';

import type { ReceivedOptionQuoteReply } from '@/api/option/type.ts';
import { mapProductNameToStrategyType, toMultipassLegId, toRfsDate } from '@/api/option/utils.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { getRfsId, selectOptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type {
  FxOption,
  MultipassLegId,
  OptionBarrier,
  OptionLegProperties,
  OptionSide,
  OptionStrike,
  OptionType,
  SettlementType,
  SolvingStrike,
} from '@/store/option/state.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import type { CurrencyChoice, PriceType } from '@/types/common.ts';
import type { ISODate } from '@/types/date.ts';
import { type IQuoteAbort, isQuoteTraderNotification, isQuoteWithPrice, type QuoteReply } from '@/utils/rfs/type.ts';

export type StrategyTypeName = 'Vanilla' | 'Trigger' | 'Binary' | 'Multileg' | 'Straddle' | 'Strangle' | 'RiskReversal';

export type TcHedgeType = 'None' | 'Spot' | 'Forward' | 'Ndf';

interface IFxoProduct {
  strategyType: StrategyTypeName;
  currencyPair: string;
  strategyNetted?: boolean;
  premiumCurrency: CurrencyChoice;
  premiumType: PriceType;
  amountReference?: number;
  amountReferenceCurrency?: string;
  markupCurrency: CurrencyChoice;
  legs: IFxoLeg[];
  hedges: IFxoHedgeRequest[];
  skipCreditCheck: boolean;
  isSolving?: boolean;
}

/**
 * TODO: check with backend why this is different from update
 * Request: https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/d861f49715798e74f81b67aa2e64ad16a4df8d74/fx-web/api/src/SgMarkets.Execution.Fx.Web.Services/Service/Mappers/CreateRequestDtoMapper.cs#L223-L222
 */
export interface IFxoHedgeRequest {
  hedgeType: TcHedgeType;
  maturityDate?: Date;
  price?: string;
  notionalAmount?: string;
  notionalCurrency: number;
}

export interface IFxoRfsCreateRequest {
  fxOptionProduct: IFxoProduct;
  requesterCounterpartyId: number;
  rfsId: string;
  forceTradable: boolean;
}

type MultipassBarrier = {
  type: 'Up' | 'Down';
  knockType: 'In' | 'Out';
  nature: 'AMERICAN' | 'EUROPEAN' | 'WINDOW';
  level: number;
  startDate: Date;
  endDate: Date;
};

interface IFxoLeg {
  id: MultipassLegId;
  side?: OptionSide;
  strikePrice?: number;
  strikeInDelta?: string;
  strikeShortcut?: string;
  notionalAmount?: number;
  notionalCurrency?: CurrencyChoice;
  expiryDate?: Date;
  deliveryDate?: Date;
  premiumDeliveryDate?: Date;
  putOrCall?: OptionType;
  settlementType?: SettlementType;
  cutOffMarketPlace?: string;
  cashSettlementCurrency?: string;
  premiumBidValue?: number;
  premiumAskValue?: number;
  markupBidValue?: number;
  markupAskValue?: number;
  volatilityAskValue?: number;
  volatilityBidValue?: number;
  crossCurrency?: string;
  fixingReference1?: string;
  fixingReference2?: string;
  product: 'Trigger' | 'Binary' | 'Vanilla';
  barriers?: MultipassBarrier[];
  inFine?: boolean;
}

interface RfsRequestBody {
  body: IFxoRfsCreateRequest;
  tileId: string;
  rfsId: string;
}

export function requestOptionRfs(builder: WebapiEndpointBuilder) {
  return builder.mutation<{ skipCreditCheck: boolean }, RfsRequestBody>({
    query: (params) => {
      return {
        url: '/fxOption/rfs/request',
        method: 'POST',
        body: params.body,
        headers: {
          'X-Correlation-Id': params.rfsId,
        },
      };
    },

    transformResponse(_baseQueryReturnValue, _meta, arg) {
      return {
        skipCreditCheck: arg.body.fxOptionProduct.skipCreditCheck,
      };
    },

    async onCacheEntryAdded(params, { cacheDataLoaded, cacheEntryRemoved, dispatch, getState }) {
      await cacheDataLoaded;

      const quoteParams: QuoteParams = {
        tileId: params.tileId,
        rfsId: params.rfsId,
        dispatch,
        getState: getState as () => AppState,
      };
      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(quoteParams);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(quoteParams);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      await cacheEntryRemoved; // TODO: remove automatic cache removed after 1 hour
      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);
    },
  });
}

interface QuoteParams {
  tileId: string;
  rfsId: string;
  dispatch: AppDispatch;
  getState: () => AppState;
}

function buildHandleReceiveQuoteAbort(quoteParams: QuoteParams) {
  return (quoteAbort: IQuoteAbort) => {
    const { dispatch, tileId } = quoteParams;
    if (isQuoteValid(quoteAbort, quoteParams)) {
      dispatch(optionSlice.actions.optionRfsQuoteAborted({ tileId, reply: quoteAbort }));
    }
  };
}

function buildHandleReceiveQuoteReply(quoteParams: QuoteParams) {
  return (reply: QuoteReply<ReceivedOptionQuoteReply>) => {
    const { dispatch, tileId } = quoteParams;
    if (!isQuoteValid(reply, quoteParams)) {
      return;
    }

    if (isQuoteWithPrice(reply)) {
      // FIXME: this makes a lot of e2e test fail
      // const option = optionSlice.selectors.getOptionTile(quoteParams.getState(), tileId);
      // if (reply.OptionQuoteReply.RequestVersion === option?.properties.rfsVersion) {
      dispatch(
        optionSlice.actions.optionRfsQuoteReceived({
          tileId,
          message: reply,
        }),
      );
      // }
    } else if (isQuoteTraderNotification(reply)) {
      dispatch(
        optionSlice.actions.optionTraderNotificationReceived({
          tileId,
          message: reply,
        }),
      );
    }
  };
}

function isQuoteValid(message: QuoteReply<ReceivedOptionQuoteReply>, quoteParams: QuoteParams): boolean {
  const { rfsId, tileId, getState } = quoteParams;
  if (message.RequestId !== rfsId) {
    return false;
  }

  const state = getState();
  const option = state.option[tileId];
  const optionRfsId = isDefined(option) ? getRfsId(option) : undefined;

  if (isNotDefined(optionRfsId)) {
    // rfs was just cancelled
    return false;
  }

  if (message.RequestId !== optionRfsId) {
    // Old request still lingering (maybe cancel was unsuccessful, or we received a quote while cancelling)
    return false;
  }
  return true;
}

interface RfsRequestParams {
  option: FxOption;
  tileId: string;
  rfsId: string;
  clientId: number;
  forceTradeable: boolean;
  skipCreditCheck?: boolean;
}

function mapStrike(
  strike: OptionStrike | SolvingStrike | undefined,
): Pick<IFxoLeg, 'strikePrice' | 'strikeInDelta' | 'strikeShortcut'> {
  if (isNotDefined(strike)) {
    return {
      strikePrice: undefined,
      strikeInDelta: undefined,
      strikeShortcut: undefined,
    };
  }

  switch (strike.type) {
    case 'solving':
      return {
        strikeShortcut: undefined,
        strikePrice: undefined,
        strikeInDelta: undefined,
      };
    case 'number':
      return {
        strikePrice: strike.value,
        strikeInDelta: undefined,
        strikeShortcut: undefined,
      };
    case 'shortcut':
      return {
        strikePrice: undefined,
        strikeInDelta: undefined,
        strikeShortcut: strike.value,
      };
    case 'delta':
      return {
        strikePrice: undefined,
        strikeInDelta: `${strike.value}d`,
        strikeShortcut: undefined,
      };
    default:
      assertUnreachable(strike, `Unknow strike type ${strike}`);
  }
}

/**
 * In risk reversal we invert both legs when we select "Sells call" (only in compact view)
 * In compact view, we deduce the side from the way
 */
function computeSolvingSide(
  leg: OptionLegProperties,
  solvingStrike: SolvingStrike | undefined,
): OptionSide | undefined {
  if (isNotDefined(solvingStrike)) {
    // no solving
    return undefined;
  }

  const solvingSide = solvingStrike.solvingSide;
  switch (solvingSide.type) {
    case 'strategy':
      return undefined;

    case 'compactView':
      return solvingSide.overrideSolveSide;

    case 'riskReversal':
      return solvingSide.solveSide === 'SellCall' ? invertSide(leg.side) : undefined;
  }
}

function invertSide(side: OptionSide | undefined): OptionSide | undefined {
  if (isNotDefined(side)) {
    return undefined;
  }
  return side === 'Buy' ? 'Sell' : 'Buy';
}

function getSolvingLeg(legs: OptionLegProperties[]): SolvingStrike | undefined {
  for (const leg of legs) {
    if (leg.strike?.type === 'solving') {
      return leg.strike;
    }
  }
  return undefined;
}

export function createRfsRequestParams({
  option,
  tileId,
  rfsId,
  clientId,
  forceTradeable,
  skipCreditCheck = false,
}: RfsRequestParams): RfsRequestBody {
  const properties = option.properties;
  const optionCurrency = selectOptionCurrency(option);

  const cashSettlementCurrency =
    properties?.cashSettlementCurrency ?? properties?.possibleCashSettlementCurrencies?.default;
  const settlementType = properties?.settlementType ?? properties?.possibleSettlementTypes?.default;
  const cutOffMarketPlace = properties?.cutOffMarketPlace ?? properties?.possibleCutOffMarketPlaces?.default;

  const { currencyPair, currency1, currency2 } = optionCurrency;
  const premiumCategory = properties.premiumType.premiumCategory;
  const premiumCurrency = properties.premiumType.premiumCurrency;

  const strategyType = mapProductNameToStrategyType(option.type, properties.legs.length);

  const markupCurrencyLabel = properties.markupCurrency;
  const markupCurrency: CurrencyChoice = markupCurrencyLabel === currency2 ? 2 : 1;
  const premiumCurrencyLabel = premiumCurrency === 2 ? currency2 : currency1;

  const hedgeType = properties.hedgeType;

  const solvingStrike = getSolvingLeg(properties.legs);

  const product: IFxoProduct = {
    currencyPair,
    strategyType,
    premiumType: premiumCategory,
    markupCurrency,
    premiumCurrency,
    skipCreditCheck,
    isSolving: properties.legs.some((leg) => leg.strike?.type === 'solving'),
    amountReference: properties.amountReference,
    amountReferenceCurrency: isDefined(properties.amountReference) ? premiumCurrencyLabel : undefined,
    legs: properties.legs.map((leg) => {
      // while solving, the requested premium is sent directly to Multipass, not through Trade Capture
      const legSolving = leg.strike?.type === 'solving' ? leg.strike : undefined;

      return {
        ...(leg.legProductName === 'Binary' ? {} : mapStrike(leg.strike)),
        id: toMultipassLegId(leg.legId, option.type),
        side: computeSolvingSide(leg, solvingStrike) ?? leg.side,
        putOrCall: leg.type,
        notionalCurrency: leg.notionalCurrencyIndex,
        notionalAmount: leg.notional,
        settlementType: settlementType,
        cutOffMarketPlace: cutOffMarketPlace,
        expiryDate: toRfsDate(leg.expiryDate),
        deliveryDate: toRfsDate(leg.deliveryDate),
        premiumDeliveryDate: toRfsDate(leg.premiumPaymentDate),
        cashSettlementCurrency: cashSettlementCurrency,
        markupAskValue: leg.markup,
        markupBidValue: leg.markup,
        premiumAskValue: isDefined(legSolving)
          ? legSolving.way === 'Ask'
            ? legSolving.solveValue
            : undefined
          : leg.premiumAsk,
        premiumBidValue: isDefined(legSolving)
          ? legSolving.way === 'Bid'
            ? legSolving.solveValue
            : undefined
          : leg.premiumBid,
        volatilityBidValue: leg.volatilityBid,
        volatilityAskValue: leg.volatilityAsk,
        crossCurrency: properties.crossCurrency,
        fixingReference1: properties.fixingReference1,
        fixingReference2: properties.fixingReference2,
        product: leg.legProductName,
        barriers: mapBarriers(leg.barrier1, leg.barrier2, leg.expiryDate),
        inFine: leg.legProductName === 'Binary' ? true : undefined,
      };
    }),
    hedges:
      hedgeType === 'Live'
        ? []
        : properties.hedges.map((hedge) => ({
            hedgeType: hedgeType,
            maturityDate: toRfsDate(hedge.maturityDate),
            price: hedge.rate?.toString(),
            notionalAmount: hedge.notionalAmount?.toString(),
            notionalCurrency: hedge.notionalCurrency ?? 1,
          })),
  };

  return {
    tileId,
    rfsId,
    body: {
      fxOptionProduct: product,
      rfsId: rfsId,
      forceTradable: forceTradeable,
      requesterCounterpartyId: clientId,
    },
  };
}

function mapBarriers(
  barrier1: OptionBarrier | undefined,
  barrier2: OptionBarrier | undefined,
  expiryDate: ISODate | undefined,
): MultipassBarrier[] | undefined {
  const barriers = [barrier1, barrier2]
    .filter(isDefined)
    .map((b) => mapBarrier(b, expiryDate))
    .filter(isDefined);
  return barriers.length > 0 ? barriers : undefined;
}

function mapBarrier(barrier: OptionBarrier, expiryDate: ISODate | undefined): MultipassBarrier | undefined {
  if (barrier.level === undefined || barrier.type === undefined || barrier.nature === undefined) {
    return undefined;
  }

  const type: MultipassBarrier['type'] = barrier.type === 'UpOut' || barrier.type === 'UpIn' ? 'Up' : 'Down';
  const knockType: MultipassBarrier['knockType'] =
    barrier.type === 'UpOut' || barrier.type === 'DownOut' ? 'Out' : 'In';
  const nature: MultipassBarrier['nature'] = mapBarrierNature(barrier.nature);
  const mappedExpiryDate = isDefined(expiryDate) ? new Date(expiryDate) : new Date();
  const { startDate, endDate } = mapBarrierDates(nature, mappedExpiryDate, barrier);

  return {
    type,
    knockType,
    nature,
    level: barrier.level,
    startDate,
    endDate,
  };
}

function mapBarrierDates(
  nature: MultipassBarrier['nature'],
  mappedExpiryDate: Date,
  barrier: OptionBarrier,
): { startDate: Date; endDate: Date } {
  switch (nature) {
    case 'EUROPEAN':
      return { startDate: mappedExpiryDate, endDate: mappedExpiryDate };
    case 'AMERICAN':
      return { startDate: new Date(), endDate: mappedExpiryDate };
    case 'WINDOW': {
      const startDate = isDefined(barrier.startDate) ? new Date(barrier.startDate) : new Date();
      const endDate = isDefined(barrier.endDate) ? new Date(barrier.endDate) : new Date();
      return { startDate, endDate };
    }
    default:
      return assertUnreachable(nature, `Unknown barrier nature: ${nature}`);
  }
}

function mapBarrierNature(nature: NonNullable<OptionBarrier['nature']>): MultipassBarrier['nature'] {
  switch (nature) {
    case 'AMER':
      return 'AMERICAN';
    case 'EURO':
      return 'EUROPEAN';
    case 'WINDOW':
      return 'WINDOW';
    default:
      return assertUnreachable(nature, `Unknown barrier nature: ${nature}`);
  }
}
